/**
 * SettingsFrame: the two-column frame of every settings-style area. A
 * sticky sub-nav on the left (a title, then links under mono group
 * headings) and the page on the right. /settings (SettingsLayout) and the
 * staff team configuration pages under /employees/teams/:teamId
 * (TeamConfigLayout) both render in it, so the two read as one product
 * and a page written with the Settings primitives drops into either.
 *
 * `lead` sits above the title (a back link) and `subtitle` under it (a
 * slug). A group with no entries is skipped along with its heading.
 */

import { NavEntryLink } from '@/components/layout/NavEntryLink'
import { PageContainer } from '@/components/ui/page-container'
import type { NavEntry } from '@/lib/nav'

export interface SettingsNavGroup {
  label: string
  entries: NavEntry[]
}

interface SettingsFrameProps {
  /** The sub-nav's accessible name. */
  navLabel: string
  title: string
  lead?: React.ReactNode
  subtitle?: string
  groups: SettingsNavGroup[]
  children: React.ReactNode
}

export function SettingsFrame({
  navLabel,
  title,
  lead,
  subtitle,
  groups,
  children,
}: SettingsFrameProps): React.JSX.Element {
  return (
    <PageContainer className="flex gap-10">
      {/* Sticky so a long page (Billing, Review scans) scrolls under a nav
          that stays put; `main` in AppLayout is the scroll container. */}
      <nav aria-label={navLabel} className="sticky top-6 w-44 shrink-0 space-y-6 self-start">
        <div className="space-y-1 px-2">
          {lead}
          <p className="break-words font-serif text-lg font-medium text-foreground">{title}</p>
          {subtitle !== undefined && (
            <p className="break-all font-mono text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {groups.map((group) => (
          <NavGroup key={group.label} group={group} />
        ))}
      </nav>
      <div className="min-w-0 flex-1">{children}</div>
    </PageContainer>
  )
}

function NavGroup({ group }: { group: SettingsNavGroup }): React.JSX.Element | null {
  if (group.entries.length === 0) return null
  return (
    <div className="space-y-1">
      <p className="px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {group.label}
      </p>
      {group.entries.map((entry) => (
        <NavEntryLink key={entry.to} entry={entry} />
      ))}
    </div>
  )
}
